import { apiClient } from '@/api/client';
import type {
  CategoryModel,
  CreateCategory,
  ItemModel,
  PaletteColorModel,
  UpdateCategoryDefinition,
} from '@/types/categories.types';

/** Admin-only endpoints (`app-admin`); renaming a system category needs `app-superadmin`. */
export const categoriesApi = {
  // GET /categories — by sort order, with what the caller may do.
  list: async (): Promise<CategoryModel[]> => {
    const { data } = await apiClient.get<CategoryModel[]>('/categories');
    return data;
  },

  // GET /categories/palette — the 16 palette colours with `free`.
  palette: async (): Promise<PaletteColorModel[]> => {
    const { data } = await apiClient.get<PaletteColorModel[]>('/categories/palette');
    return data;
  },

  // POST /categories → 201; 409 label taken / colour not free; 422 label < 3 or colour not in the palette.
  create: async (body: CreateCategory): Promise<CategoryModel> => {
    const { data } = await apiClient.post<CategoryModel>('/categories', body);
    return data;
  },

  // PATCH /categories/{key} → 200; 403 system category as admin; 409 label taken.
  update: async (key: string, body: UpdateCategoryDefinition): Promise<CategoryModel> => {
    const { data } = await apiClient.patch<CategoryModel>(`/categories/${key}`, body);
    return data;
  },

  // DELETE /categories/{key} → 204; 409 for a system category. Items stay, memberships go.
  remove: async (key: string): Promise<void> => {
    await apiClient.delete(`/categories/${key}`);
  },

  // POST /categories/{key}/items → 200 with the listed items (bulk attach).
  attachItems: async (key: string, itemIds: string[]): Promise<ItemModel[]> => {
    const body = { item_ids: itemIds };
    const { data } = await apiClient.post<ItemModel[]>(`/categories/${key}/items`, body);
    return data;
  },
};
